import { AppBar, Badge, Box, Button, createMuiTheme, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, ListItemIcon, ListItemText, makeStyles, Menu, MenuItem, Theme, ThemeProvider, Toolbar, Typography } from "@material-ui/core";
import { blue, grey, pink } from "@material-ui/core/colors";
import Slide from '@material-ui/core/Slide';
import Snackbar from '@material-ui/core/Snackbar';
import AccountCircle from '@material-ui/icons/AccountCircle';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import GetAppIcon from '@material-ui/icons/GetApp';
import AboutIcon from '@material-ui/icons/InfoOutlined';
import NotificationsIcon from '@material-ui/icons/NotificationsNoneOutlined';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOffOutlined';
import TranslateOutlinedIcon from '@material-ui/icons/TranslateOutlined';
import MuiAlert from '@material-ui/lab/Alert';
import React, { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import { useGetVapidPublicKeyMutation, useIsLoggedInQuery, useLogoutMutation, useRegisterPushSubscriptionMutation } from "../graphql/generated/graphql";
import useIsPWAInstalled from "./Hooks/useIsPWAInstalled";
import usePaletteType from "./Hooks/usePaletteType";
import { ServiceWorkerState } from './Hooks/useServiceWorker';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  grow: {
    flexGrow: 1,
  },
  aboutDialog: {
    '& a': {
      color: theme.palette.primary.light,
    },
  }
}));

const Component: FunctionComponent<{ serviceWorkerState: ServiceWorkerState, env: string }> = props =>
{
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const menuId = 'primary-search-account-menu';

  const { data: isLoggedInQueryData } = useIsLoggedInQuery();
  const location = useLocation();
  const history = useHistory();

  const onLoginClick = async () =>
  {
    setAnchorEl(null);
    history.replace(`/login?returnurl=${location.pathname}`);
  };

  const [logout] = useLogoutMutation();

  const onLogoutClick = async () =>
  {
    setAnchorEl(null);
    if(pushSubscription)
    {
      try
      {
        await pushSubscription.unsubscribe();
        setPushSubscription(null);
      } catch(err)
      {
      }
    }
    await logout();
    localStorage.removeItem('userDisplayName');
  };

  const handleProfileMenuOpen = async (event: React.MouseEvent<HTMLElement>) =>
  {
    setAnchorEl(event.currentTarget);

    if('serviceWorker' in navigator)
    {
      try
      {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
      } catch(err) { }
    }
  };

  const handleMenuClose = () =>
  {
    setAnchorEl(null);
  };

  const [refreshApp, setRefreshApp] = useState(false);

  useEffect(() =>
  {
    if(refreshApp)
    {
      (async () =>
      {
        const registration = await navigator.serviceWorker.ready;
        await registration.unregister();
        window.location.reload();
      })();
    }
  }, [refreshApp]);

  const onRefreshApp = () =>
  {
    setAnchorEl(null);
    setRefreshApp(true);
  };

  const [refreshSW, setRefreshSW] = useState(false);

  useEffect(() =>
  {
    if(refreshSW)
    {
      setRefreshSW(false);
      (async () =>
      {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
      })();
    }
  }, [refreshSW]);

  const paletteType = usePaletteType();

  const palettes = {
    primary: {
      dark: {
        contrastText: grey[300],
        main: '#35363a',
      },
      light: {
        contrastText: 'white',
        main: blue[500],
      },
    },
    secondary: {
      dark: { main: blue[500] },
      light: { main: pink[200] },
    },
    text: {
      primary: {
        dark: grey[300],
        light: '#000',
      },
    },
  }

  const theme = createMuiTheme({
    palette: {
      primary: palettes.primary[paletteType],
      secondary: palettes.secondary[paletteType],
      text: {
        primary: palettes.text.primary[paletteType],
      },
      type: paletteType,
    },
    typography: {
    },
  });

  const languages: { [x: string]: { nativeName: string } } = {
    en: { nativeName: 'English' },
    hu: { nativeName: 'Magyar' },
  };

  useEffect(() =>
  {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const [pushSubscription, setPushSubscription] = useState<PushSubscription | null>(null);
  const [isNotificationButtonEnabled, setIsNotificationButtonEnabled] = useState(false);
  const [snackbarState, setSnackbarState] = useState<{
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  } | null>(null);

  useEffect(() =>
  {
    // On mount, check for existing push subscription
    if('serviceWorker' in navigator && 'PushManager' in window)
    {
      navigator.serviceWorker.ready.then(registration =>
      {
        registration.pushManager.getSubscription().then(subscription =>
        {
          setPushSubscription(subscription);
          setIsNotificationButtonEnabled(true);
        });
      });
    }
  }, []);

  const [getVAPUDPublicKey] = useGetVapidPublicKeyMutation();
  const [registerPushSubscription] = useRegisterPushSubscriptionMutation();

  const subscribeUserToPush = async () =>
  {
    if('serviceWorker' in navigator && 'PushManager' in window)
    {
      try
      {
        const registration = await navigator.serviceWorker.ready;
        const vapidPublicKey = (await getVAPUDPublicKey()).data!.GetVAPIDPublicKey;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidPublicKey
        });
        setPushSubscription(subscription);
        const subscriptionJSON = subscription.toJSON();
        const registrationResult = await registerPushSubscription({
          variables: {
            Subscription: {
              endpoint: subscriptionJSON.endpoint!,
              keys: {
                p256dh: subscriptionJSON.keys!.p256dh,
                auth: subscriptionJSON.keys!.auth,
              },
            },
          },
        });
        if(registrationResult.data?.RegisterPushSubscription)
        {
          showMessage(t('layout.notifications.subscriptionSuccess'), 'success');
        }
        else
        {
          showMessage(t('layout.notifications.subscriptionFailed'), 'error');
        }
      } catch(err)
      {
        // Handle errors
      }
      setIsNotificationButtonEnabled(true);
    }
  };

  const handleNotificationsIconClick = async () =>
  {
    setIsNotificationButtonEnabled(false);
    if(!('serviceWorker' in navigator && 'PushManager' in window))
    {
      showMessage(t('layout.notifications.noPushSupport'), 'error');
      setIsNotificationButtonEnabled(true);
      return;
    }
    try
    {
      const registration = await navigator.serviceWorker.getRegistration();
      if(!registration || !navigator.serviceWorker.controller)
      {
        showMessage(t('layout.notifications.noServiceWorker'), 'error');
        setIsNotificationButtonEnabled(true);
        return;
      }
      if(!pushSubscription)
      {
        const permission = await Notification.requestPermission();
        if(permission === 'granted')
        {
          await subscribeUserToPush();
        }
      }
    } catch(err)
    {
      showMessage(t('layout.notifications.noServiceWorker'), 'error');
    }
    setIsNotificationButtonEnabled(true);
  }

  const showMessage = (message: string, severity: 'success' | 'error' | 'info' | 'warning') =>
  {
    setSnackbarState({ message, severity });
  };

  useEffect(() =>
  {
    const wasLoggedIn = localStorage.getItem('prevIsLoggedIn') === 'true';
    const isLoggedIn = isLoggedInQueryData?.isLoggedIn;
    if(
      wasLoggedIn === false &&
      isLoggedIn === true &&
      Notification.permission === 'granted' &&
      !pushSubscription &&
      'serviceWorker' in navigator && 'PushManager' in window
    )
    {
      subscribeUserToPush();
    }
    localStorage.setItem('prevIsLoggedIn', isLoggedIn ? 'true' : 'false');
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedInQueryData?.isLoggedIn, pushSubscription]);

  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);

  const isPWAInstalled = useIsPWAInstalled();

  useEffect(() =>
  {
    const handleBeforeInstallPrompt = (e: Event) =>
    {
      e.preventDefault();
      (window as any).deferredPWAInstallPrompt = e;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () =>
    {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const installPWA = () =>
  {
    (window as any).deferredPWAInstallPrompt.prompt();
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <AppBar position="sticky">
          <Toolbar variant="dense">
            <Typography variant="h6" noWrap>
              {process.env.REACT_APP_NAME}
            </Typography>
            <div className={classes.grow} />
            {isLoggedInQueryData?.isLoggedIn ?
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleNotificationsIconClick}
                disabled={!isNotificationButtonEnabled}
              >
                {pushSubscription ? <NotificationsIcon /> : <NotificationsOffIcon />}
              </IconButton>
              : null}
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleProfileMenuOpen}
            >
              <Badge badgeContent={props.serviceWorkerState === ServiceWorkerState.UpdateAvailable ? 1 : 0} color="secondary">
                <AccountCircle />
              </Badge>
            </IconButton>
          </Toolbar>
        </AppBar>
        <Dialog open={isAboutDialogOpen} onClose={() => setIsAboutDialogOpen(false)} className={classes.aboutDialog}>
          <DialogTitle>{t('layout.about.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Box display="flex" alignItems="flex-start">
                <Box>
                  <Box dangerouslySetInnerHTML={{ __html: t('layout.about.description') }} />
                  <Box>
                    {t('layout.about.version')}: {props.env} {process.env.REACT_APP_BUILDNUMBER}<br />
                  </Box>
                </Box>
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsAboutDialogOpen(false)} variant="outlined">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
      <Box className={classes.root}>
        <>{props.children}</>
      </Box>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        id={menuId}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        {!isPWAInstalled && 'serviceWorker' in navigator && (window as any).deferredPWAInstallPrompt ?
          <MenuItem onClick={() => { handleMenuClose(); installPWA(); }}>
            <ListItemIcon>
              <GetAppIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary={t('layout.profile.menu.install')} />
          </MenuItem>
          : null}
        {props.serviceWorkerState === ServiceWorkerState.UpdateAvailable ?
          <MenuItem onClick={onRefreshApp}>
            <ListItemIcon>
              <GetAppIcon color="secondary" />
            </ListItemIcon>
            <ListItemText primary={t('layout.profile.menu.update')} />
          </MenuItem>
          : null}
        {Object.keys(languages).filter(l => !i18n.language.startsWith(l)).map((lang) =>
          <MenuItem key={lang} onClick={() => { handleMenuClose(); i18n.changeLanguage(lang); }}>
            <ListItemIcon>
              <TranslateOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={languages[lang].nativeName} />
          </MenuItem>
        )}
        {isLoggedInQueryData?.isLoggedIn ?
          <MenuItem onClick={onLogoutClick}>
            <ListItemIcon>
              <ArrowBackIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('layout.profile.menu.logout')} />
          </MenuItem>
          :
          <MenuItem onClick={onLoginClick}>
            <ListItemIcon>
              <ArrowForwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('layout.profile.menu.login')} />
          </MenuItem>}
        <Divider />
        <MenuItem onClick={() => { handleMenuClose(); setIsAboutDialogOpen(true); }}>
          <ListItemIcon>
            <AboutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('layout.profile.menu.about')} />
        </MenuItem>
      </Menu>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={!!snackbarState}
        autoHideDuration={6000}
        onClose={() => setSnackbarState(null)}
        style={{ marginBottom: 48 }}
        TransitionComponent={(props) => <Slide {...props} direction="up" />}
      >
        <MuiAlert elevation={6} variant="filled" severity={snackbarState?.severity || 'info'} onClose={() => setSnackbarState(null)}>
          {snackbarState?.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default Component;
